import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Badge, Button, Spinner, Alert, Tabs, Tab, Form, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaEdit, FaUser, FaIdCard, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Try to fetch user by ID
        let userData = null;
        let fetchSuccessful = false;
        
        try {
          const response = await axios.get(`https://rumahakrilik.id/api/hrd/users/${userId}/`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
          });
          
          if (response.data) {
            userData = response.data;
            fetchSuccessful = true;
          }
        } catch (err) {
          console.warn(`Gagal fetch data user ${userId}:`, err.message);
        }
        
        // Saat API gagal, gunakan data dummy
        if (!fetchSuccessful) {
          userData = {
            id: parseInt(userId),
            username: `user${userId}`,
            first_name: 'Nama',
            last_name: 'Karyawan',
            email: `user${userId}@rumahakrilik.id`,
            phone: `08${Math.floor(Math.random() * 10000000000)}`,
            role: { id: 1, name: 'Staff' },
            department: 'Marketing',
            is_active: true,
            joining_date: '2022-05-15',
            address: 'Jl. Contoh No. 123, Jakarta',
            emergency_contact: '0812-3456-7890',
            birth_date: '1990-01-01',
            education: 'S1 Teknik',
            skills: 'Design, Communication',
            notes: 'Karyawan berprestasi'
          };
          console.log("Menggunakan data user dummy");
        }
        
        setUser(userData);
        setEditFormData(userData);
        
        // Fetch roles and departments for editing
        fetchRolesAndDepartments();
      } catch (err) {
        console.error(`Error fetching user ${userId}:`, err);
        setError(`Gagal memuat data karyawan. ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [userId]);

  // Fetch roles and departments for dropdown options
  const fetchRolesAndDepartments = async () => {
    try {
      // Try to fetch roles
      let rolesData = [];
      try {
        const response = await axios.get('https://rumahakrilik.id/api/hrd/roles/', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
        });
        
        if (response.data && Array.isArray(response.data)) {
          rolesData = response.data;
        }
      } catch (err) {
        console.warn("Gagal fetch roles:", err.message);
        // Fallback roles
        rolesData = [
          { id: 1, name: 'owner' },
          { id: 2, name: 'general manager' },
          { id: 3, name: 'supervisor marketing' },
          { id: 4, name: 'cs online' },
          { id: 5, name: 'finishing' },
          { id: 6, name: 'admin' },
          { id: 7, name: 'hrd' }
        ];
      }
      
      // Try to fetch departments
      let departmentsData = [];
      try {
        const response = await axios.get('https://rumahakrilik.id/api/hrd/departments/', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
        });
        
        if (response.data && Array.isArray(response.data)) {
          departmentsData = response.data;
        }
      } catch (err) {
        console.warn("Gagal fetch departments:", err.message);
        // Fallback departments
        departmentsData = [
          { id: 1, name: 'Management' },
          { id: 2, name: 'Marketing' },
          { id: 3, name: 'Production' },
          { id: 4, name: 'Finance' },
          { id: 5, name: 'Human Resources' },
          { id: 6, name: 'Customer Service' }
        ];
      }
      
      setRoles(rolesData);
      setDepartments(departmentsData);
    } catch (err) {
      console.error("Error fetching roles and departments:", err);
      toast.error("Gagal memuat data role dan departemen");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Try API
      try {
        const response = await axios.put(`https://rumahakrilik.id/api/hrd/users/${userId}/`, editFormData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
        });
        
        if (response.data) {
          setUser(response.data);
          toast.success('Data karyawan berhasil diperbarui');
        }
      } catch (err) {
        console.warn("Gagal update user ke API, update secara lokal");
        
        // Fallback - update locally
        setUser(editFormData);
        toast.info('Data karyawan diperbarui secara lokal (server tidak tersedia)');
      }
      
      setIsEditing(false);
    } catch (err) {
      console.error(`Error updating user ${userId}:`, err);
      toast.error(`Gagal memperbarui data karyawan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Render component
  if (loading && !user) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Memuat data karyawan...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error}
          <div className="mt-3">
            <Button variant="primary" onClick={() => navigate('/hrd/users')}>
              Kembali ke Daftar Karyawan
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          Karyawan dengan ID {userId} tidak ditemukan.
          <div className="mt-3">
            <Button variant="primary" onClick={() => navigate('/hrd/users')}>
              Kembali ke Daftar Karyawan
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <Button 
                variant="outline-secondary" 
                className="me-3"
                onClick={() => navigate(-1)}
              >
                &lt; Kembali
              </Button>
              <h2 className="mb-0">Profil Karyawan</h2>
            </div>
            <Button 
              variant="primary"
              onClick={() => setIsEditing(!isEditing)}
            >
              <FaEdit className="me-1" /> {isEditing ? 'Batal Edit' : 'Edit Profil'}
            </Button>
          </div>

          {isEditing ? (
            <Form onSubmit={handleUpdateUser}>
              <Row>
                <Col md={3} className="text-center mb-4">
                  <div className="avatar-placeholder mb-3">
                    {user.first_name?.[0]?.toUpperCase() || 
                     user.username?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="is_active"
                      value={editFormData.is_active}
                      onChange={handleInputChange}
                    >
                      <option value={true}>Aktif</option>
                      <option value={false}>Non-aktif</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={9}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          value={editFormData.username || ''}
                          onChange={handleInputChange}
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={editFormData.password || ''}
                          onChange={handleInputChange}
                          placeholder="Biarkan kosong jika tidak ingin mengubah"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nama Depan</Form.Label>
                        <Form.Control
                          type="text"
                          name="first_name"
                          value={editFormData.first_name || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nama Belakang</Form.Label>
                        <Form.Control
                          type="text"
                          name="last_name"
                          value={editFormData.last_name || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={editFormData.email || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Telepon</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone"
                          value={editFormData.phone || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Role</Form.Label>
                        <Form.Select
                          name="role_id"
                          value={editFormData.role_id || editFormData.role?.id || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">Pilih Role</option>
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Departemen</Form.Label>
                        <Form.Select
                          name="department"
                          value={editFormData.department || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">Pilih Departemen</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.name}>
                              {dept.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tanggal Bergabung</Form.Label>
                        <Form.Control
                          type="date"
                          name="joining_date"
                          value={editFormData.joining_date || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tanggal Lahir</Form.Label>
                        <Form.Control
                          type="date"
                          name="birth_date"
                          value={editFormData.birth_date || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Alamat</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="address"
                      value={editFormData.address || ''}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Kontak Darurat</Form.Label>
                        <Form.Control
                          type="text"
                          name="emergency_contact"
                          value={editFormData.emergency_contact || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Pendidikan</Form.Label>
                        <Form.Control
                          type="text"
                          name="education"
                          value={editFormData.education || ''}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Keahlian</Form.Label>
                    <Form.Control
                      type="text"
                      name="skills"
                      value={editFormData.skills || ''}
                      onChange={handleInputChange}
                      placeholder="Pisahkan dengan koma, contoh: Design, Communication, Leadership"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Catatan</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={editFormData.notes || ''}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  
                  <div className="text-end">
                    <Button 
                      variant="secondary" 
                      className="me-2"
                      onClick={() => setIsEditing(false)}
                    >
                      Batal
                    </Button>
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          ) : (
            <Tabs defaultActiveKey="info" className="mb-4">
              <Tab eventKey="info" title="Informasi Utama">
                <Row>
                  <Col md={3} className="text-center mb-4">
                    <div className="avatar-placeholder mb-3">
                      {user.first_name?.[0]?.toUpperCase() || 
                       user.username?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <h4>{user.first_name || user.last_name ? 
                      `${user.first_name || ''} ${user.last_name || ''}`.trim() : 
                      user.username}</h4>
                    <div className="mb-2">
                      <Badge bg={user.role?.name ? 'info' : 'secondary'} className="me-2">
                        {user.role?.name || 'Staff'}
                      </Badge>
                      <Badge bg={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? 'Aktif' : 'Non-aktif'}
                      </Badge>
                    </div>
                    <div className="mb-3">
                      <Badge bg="light" text="dark">
                        {user.department || 'Belum ditentukan'}
                      </Badge>
                    </div>
                    <div className="d-flex flex-column align-items-center mb-2">
                      <div className="mb-2">
                        <FaCalendarAlt className="me-2" />
                        <span>Bergabung: {user.joining_date || 'Tidak diketahui'}</span>
                      </div>
                      {user.birth_date && (
                        <div>
                          <FaCalendarAlt className="me-2" />
                          <span>Lahir: {user.birth_date}</span>
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={9}>
                    <Card className="mb-3">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">Informasi Kontak</h5>
                      </Card.Header>
                      <Card.Body>
                        <Row className="mb-2">
                          <Col sm={1} className="text-center">
                            <FaIdCard size={18} />
                          </Col>
                          <Col sm={3} className="fw-bold">Username</Col>
                          <Col sm={8}>{user.username}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col sm={1} className="text-center">
                            <FaEnvelope size={18} />
                          </Col>
                          <Col sm={3} className="fw-bold">Email</Col>
                          <Col sm={8}>{user.email || '-'}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col sm={1} className="text-center">
                            <FaPhone size={18} />
                          </Col>
                          <Col sm={3} className="fw-bold">Telepon</Col>
                          <Col sm={8}>{user.phone || '-'}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col sm={1} className="text-center">
                            <FaPhone size={18} />
                          </Col>
                          <Col sm={3} className="fw-bold">Kontak Darurat</Col>
                          <Col sm={8}>{user.emergency_contact || '-'}</Col>
                        </Row>
                        <Row>
                          <Col sm={1} className="text-center">
                            <FaMapMarkerAlt size={18} />
                          </Col>
                          <Col sm={3} className="fw-bold">Alamat</Col>
                          <Col sm={8}>{user.address || '-'}</Col>
                        </Row>
                      </Card.Body>
                    </Card>
                    
                    <Card className="mb-3">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">Informasi Pekerjaan</h5>
                      </Card.Header>
                      <Card.Body>
                        <Row className="mb-2">
                          <Col md={3} className="fw-bold">Departemen</Col>
                          <Col md={9}>{user.department || '-'}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md={3} className="fw-bold">Jabatan</Col>
                          <Col md={9}>{user.role?.name || '-'}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md={3} className="fw-bold">Tanggal Bergabung</Col>
                          <Col md={9}>{user.joining_date || '-'}</Col>
                        </Row>
                        <Row className="mb-2">
                          <Col md={3} className="fw-bold">Status</Col>
                          <Col md={9}>
                            <Badge bg={user.is_active ? 'success' : 'secondary'}>
                              {user.is_active ? 'Aktif' : 'Non-aktif'}
                            </Badge>
                          </Col>
                        </Row>
                        {user.education && (
                          <Row className="mb-2">
                            <Col md={3} className="fw-bold">Pendidikan</Col>
                            <Col md={9}>{user.education}</Col>
                          </Row>
                        )}
                        {user.skills && (
                          <Row>
                            <Col md={3} className="fw-bold">Keahlian</Col>
                            <Col md={9}>
                              {user.skills.split(',').map((skill, index) => (
                                <Badge bg="secondary" className="me-1 mb-1" key={index}>
                                  {skill.trim()}
                                </Badge>
                              ))}
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                    
                    {user.notes && (
                      <Card>
                        <Card.Header className="bg-light">
                          <h5 className="mb-0">Catatan</h5>
                        </Card.Header>
                        <Card.Body>
                          <p>{user.notes}</p>
                        </Card.Body>
                      </Card>
                    )}
                  </Col>
                </Row>
              </Tab>
              
              <Tab eventKey="attendance" title="Kehadiran">
                <div className="p-3">
                  <h5>Riwayat Kehadiran</h5>
                  <Alert variant="info">
                    Data kehadiran belum tersedia di sistem.
                  </Alert>
                </div>
              </Tab>
              
              <Tab eventKey="performance" title="Performa">
                <div className="p-3">
                  <h5>Evaluasi Performa</h5>
                  <Alert variant="info">
                    Data evaluasi performa belum tersedia di sistem.
                  </Alert>
                </div>
              </Tab>
              
              <Tab eventKey="documents" title="Dokumen">
                <div className="p-3">
                  <h5>Dokumen Karyawan</h5>
                  <Alert variant="info">
                    Belum ada dokumen yang terunggah untuk karyawan ini.
                  </Alert>
                </div>
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>
      
      <style jsx="true">{`
        .avatar-placeholder {
          width: 120px;
          height: 120px;
          background-color: #6c757d;
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 48px;
          border-radius: 50%;
          margin: 0 auto;
        }
      `}</style>
    </Container>
  );
};

export default UserProfile;